import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  HStack,
  Button,
  Text,
  Icon,
  Spinner,
  VStack,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { FormProvider, useForm } from "react-hook-form";
import { algoliasearch, SearchClient } from "algoliasearch";
import actions from "src/actions";
import { AlgoliaIndexType } from "common/interfaces/Algolia";
import { CloseIcon } from "@chakra-ui/icons";
import type { KYDAudienceData } from "@common/interfaces/KYDMarketing";
import { BasicInfoStep, LookalikeStep } from "./steps";
import {
  canProceedKydAudienceFormStep1,
  formValuesToKydAudienceData,
} from "./createCustomAudienceForm";
import { createCustomAudienceFormDefaults } from "./createCustomAudienceFormDefaults";
import { createCustomAudienceLookalikeOptions } from "./createCustomAudienceLookalikeOptions";
import {
  CreateCustomAudienceFormValues,
  StepConfig,
  type StepProps,
} from "./types";
import { KYD_AUDIENCE_DIALOG_STYLES } from "./kydAudienceDialogStyles";

const dialogStyles = KYD_AUDIENCE_DIALOG_STYLES;

export type CreateCustomAudienceFormBodyProps = {
  onCreate: (audience: KYDAudienceData) => void;
  onClose: () => void;
  onBackToSource?: () => void;
};

const CreateCustomAudienceFormBody = ({
  onCreate,
  onClose,
  onBackToSource,
}: CreateCustomAudienceFormBodyProps) => {
  const toast = useToast();
  const form = useForm<CreateCustomAudienceFormValues>({
    defaultValues: createCustomAudienceFormDefaults(),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const [eventsAlgoliaConfig, setEventsAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  } | null>(null);

  const [segmentsAlgoliaConfig, setSegmentsAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  } | null>(null);

  const steps: StepConfig[] = [
    {
      id: 1,
      title: "Create audience with KYD data",
      subtitle:
        "Choose one: use an existing segment OR build a new audience with filters",
      component: BasicInfoStep,
      nextButtonText: "Next",
      canProceed: (f) => canProceedKydAudienceFormStep1(f.getValues()),
    },
    {
      id: 2,
      title: "Create lookalike audience (optional)",
      subtitle:
        "Create a lookalike audience based on your custom audience to reach similar people. You can skip this step.",
      component: LookalikeStep,
      nextButtonText: "Done",
      canProceed: () => true,
    },
  ];

  const currentStep = steps[currentStepIndex];
  const isLookalikeStep = currentStepIndex === 1;
  const lookalikeRatio = form.watch("lookalike.ratio");
  form.watch(["name", "segment", "genres", "artists"]);
  const canProceedStep1 = canProceedKydAudienceFormStep1(form.getValues());

  const fetchEventsAlgoliaConfig = useCallback(async () => {
    const response = await actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.EVENTS,
    });
    if (response.events_config) {
      const search = algoliasearch(
        response.events_config.app_id,
        response.events_config.api_key
      );
      search.clearCache();
      setEventsAlgoliaConfig({
        indexName: response.events_config.index_name,
        searchClient: search,
      });
    }
  }, []);

  const fetchSegmentsAlgoliaConfig = useCallback(async () => {
    const response = await actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.SEGMENTS,
    });
    if (response.events_config) {
      const search = algoliasearch(
        response.events_config.app_id,
        response.events_config.api_key
      );
      search.clearCache();
      setSegmentsAlgoliaConfig({
        indexName: response.events_config.index_name,
        searchClient: search,
      });
    }
  }, []);

  const resetForm = useCallback(() => {
    form.reset(createCustomAudienceFormDefaults());
    setCurrentStepIndex(0);
  }, [form]);

  const handleBack = () => {
    if (currentStepIndex === 0) {
      onBackToSource?.();
      return;
    }
    setCurrentStepIndex(0);
  };

  const handleNext = useCallback(async () => {
    setIsLoading(true);
    try {
      if (currentStepIndex < steps.length - 1) {
        const audienceName = form.getValues("name");
        if (!form.getValues("lookalike.name")?.trim()) {
          form.setValue("lookalike.name", `Lookalike ${audienceName}`);
        }
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentStepIndex, form, steps.length]);

  const handleSubmit = useCallback(
    async (data: CreateCustomAudienceFormValues) => {
      setIsLoading(true);
      try {
        onCreate(formValuesToKydAudienceData(data));
        toast({
          title: "Success",
          description: "Custom audience created successfully",
          status: "success",
        });
        resetForm();
        onClose();
      } catch (error) {
        console.error("Error creating audience:", error);
        toast({
          title: "Error",
          description: "Failed to create audience",
          status: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [onCreate, onClose, resetForm, toast]
  );

  const handleSkipLookalike = () => {
    form.setValue("lookalike", { name: "", ratio: undefined });
    form.handleSubmit(handleSubmit)();
  };

  const handlePrimaryAction = () => {
    if (currentStepIndex < steps.length - 1) {
      form.handleSubmit(() => handleNext())();
    } else {
      form.handleSubmit(handleSubmit)();
    }
  };

  useEffect(() => {
    fetchEventsAlgoliaConfig();
    fetchSegmentsAlgoliaConfig();
  }, [fetchEventsAlgoliaConfig, fetchSegmentsAlgoliaConfig]);

  const lookalikeOptions = createCustomAudienceLookalikeOptions();
  const StepComponent = currentStep.component;

  if (!eventsAlgoliaConfig) {
    return (
      <VStack
        h="full"
        w="full"
        justifyContent="center"
        alignItems="center"
        flexGrow={1}
        minH="40vh"
      >
        <Spinner />
      </VStack>
    );
  }

  return (
    <FormProvider {...form}>
      <Box
        px={6}
        id="custom-audience-form"
        data-testid="kyd-audience-dialog"
        fontFamily={dialogStyles.fontFamily}
      >
        <Box pt={6} pb={2}>
          <HStack justify="space-between" align="flex-start">
            <Box flex={1} pr={4}>
              <Text
                fontSize={dialogStyles.titleFontSize}
                fontWeight={dialogStyles.titleFontWeight}
                color={dialogStyles.titleColor}
                lineHeight={dialogStyles.titleLineHeight}
              >
                {currentStep.title}
              </Text>
              {currentStep.subtitle ? (
                <Text
                  fontSize={dialogStyles.subtitleFontSize}
                  color={dialogStyles.subtitleColor}
                  fontWeight={dialogStyles.subtitleFontWeight}
                  lineHeight={dialogStyles.subtitleLineHeight}
                  mt={1}
                >
                  {currentStep.subtitle}
                </Text>
              ) : null}
            </Box>
            <IconButton
              aria-label="Close"
              icon={<CloseIcon color="gray.500" fontSize={12} />}
              onClick={() => {
                resetForm();
                onClose();
              }}
              variant="ghost"
              color="gray.400"
              size="xs"
            />
          </HStack>
        </Box>

        <StepComponent
          eventsAlgoliaConfig={eventsAlgoliaConfig}
          segmentsAlgoliaConfig={segmentsAlgoliaConfig}
          lookalikeOptions={lookalikeOptions}
        />

        <HStack justify="space-between" spacing={3} mt={4} pb={6}>
          {(onBackToSource || isLookalikeStep) && (
            <Button
              onClick={handleBack}
              variant="outline"
              borderColor="#e2e2e2"
              bg="white"
              color={dialogStyles.titleColor}
              leftIcon={<Icon as={FiArrowLeft} />}
              rounded="10px"
              fontSize="16px"
              fontWeight="medium"
              boxShadow="0px 2px 3px rgba(0, 0, 0, 0.05)"
              _hover={{ bg: "gray.50" }}
            >
              ← Back
            </Button>
          )}
          <HStack spacing={3} ml="auto">
            {isLookalikeStep && (
              <Button
                onClick={handleSkipLookalike}
                variant="ghost"
                rounded="lg"
                fontSize="16px"
                fontWeight="semibold"
                isLoading={isLoading}
              >
                Skip
              </Button>
            )}
            <Button
              onClick={handlePrimaryAction}
              bgColor={isLookalikeStep ? "green.500" : dialogStyles.titleColor}
              color="white"
              _hover={{
                bgColor: isLookalikeStep ? "green.600" : "black",
              }}
              isDisabled={
                isLookalikeStep ? !lookalikeRatio : !canProceedStep1
              }
              rounded="8px"
              fontSize="16px"
              fontWeight="semibold"
              isLoading={isLoading}
              boxShadow="0px 10px 7.5px #f7f7f7"
              rightIcon={
                !isLookalikeStep ? <Icon as={FiArrowRight} ml={2} /> : undefined
              }
            >
              {isLookalikeStep ? "Done" : "Next →"}
            </Button>
          </HStack>
        </HStack>
      </Box>
    </FormProvider>
  );
};

export default CreateCustomAudienceFormBody;
